/**
 * Standalone Sticky ATC Bar Functionality
 */

class StickyATC {
  constructor() {
    this.stickyATC = document.querySelector('.sticky-atc-button');
    this.mainATCButton = document.querySelector('.subscription-add-to-cart') || document.querySelector('.product-form__submit');
    this.footer = document.querySelector('site-footer');
    this.stickyATCButton = document.querySelector('.sticky-atc-button__atc');
    this.subscriptionSelector = document.querySelector('.subscription-product-selector');
    this.stickySelect = document.querySelector('.sticky-atc-subscription-select');
    this.stickySelectContainer = document.querySelector('.sticky-atc-button__subscription-selector');
    
    this.isStickyActive = false;
    this.formVisible = true;
    this.formAboveViewport = false;
    this.subscriptionOptions = [];
    this.currentSelection = null;

    if (this.stickyATC && this.mainATCButton) {
      this.init();
    }
  }

  init() {
    // Set up sticky ATC height CSS variable
    this.setStickyATCHeight();
    window.addEventListener('resize', this.setStickyATCHeight.bind(this));

    // Set up intersection observer for main ATC button
    this.initObserver();

    // Connect sticky ATC button click to subscription functionality
    this.connectStickyButton();

    // Initialize subscription functionality if subscription selector exists
    if (this.subscriptionSelector) {
      this.initSubscriptionIntegration();
    }

    console.log('Sticky ATC initialized with button:', this.mainATCButton);
  }

  setStickyATCHeight() {
    if (!this.stickyATC) return;
    const htmlEl = document.documentElement;
    const stickyHeight = this.stickyATC.offsetHeight;
    htmlEl.style.setProperty('--sticky-atc-height', `${stickyHeight}px`);
  }

  isFooterVisible() {
    if (!this.footer) return false;
    const rect = this.footer.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom >= 0;
  }

  setSticky(active) {
    if (active === this.isStickyActive) return;
    this.isStickyActive = active;
    this.stickyATC?.classList.toggle('actived', active);
    document.body.classList.toggle('sticky-atc-active', active);
  }

  updateStickyState() {
    if (this.isFooterVisible()) {
      this.setSticky(false);
      return;
    }

    this.setSticky(!this.formVisible && this.formAboveViewport);
  }

  initObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.formVisible = entry.isIntersecting;
        this.formAboveViewport = entry.boundingClientRect.top < 0;
        this.updateStickyState();
      });
    }, { root: null, rootMargin: '0px', threshold: 0 });

    observer.observe(this.mainATCButton);
    window.addEventListener('scroll', this.updateStickyState.bind(this));
  }

  connectStickyButton() {
    if (!this.stickyATCButton) return;

    this.stickyATCButton.addEventListener('click', (evt) => {
      evt.preventDefault();
      
      // If we have a subscription selector, trigger its button
      if (this.mainATCButton && this.mainATCButton.classList.contains('subscription-add-to-cart')) {
        this.mainATCButton.click();
      } else {
        // Fallback for regular product forms
        const form = document.querySelector('product-form form');
        if (form) {
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      }
    });
  }

  initSubscriptionIntegration() {
    // Show the select dropdown
    if (this.stickySelectContainer) {
      this.stickySelectContainer.style.display = 'block';
    }

    // Hide price elements since pricing is shown in select options
    const priceElements = this.stickyATC.querySelectorAll('.price--sticky');
    priceElements.forEach(element => {
      element.style.display = 'none';
    });

    // Populate select options from radio buttons
    this.populateSelectOptions();

    // Listen for subscription option changes
    document.addEventListener('subscription-option-changed', this.handleSubscriptionChange.bind(this));
    
    // Listen for variant changes to update select options
    document.addEventListener('variant-changed', this.handleVariantChange.bind(this));

    // Handle select dropdown changes
    if (this.stickySelect) {
      this.stickySelect.addEventListener('change', this.handleSelectChange.bind(this));
    }

    // Set initial selection
    this.syncFromRadioButtons();
  }

  populateSelectOptions() {
    if (!this.stickySelect || !this.subscriptionSelector) return;

    const radioButtons = Array.from(this.subscriptionSelector.querySelectorAll('input[type="radio"]')).filter((radio) => {
      const optionContainer = radio.closest('.subscription-option');
      return optionContainer && !optionContainer.classList.contains('hidden');
    });
    this.stickySelect.innerHTML = '';
    this.subscriptionOptions = [];

    radioButtons.forEach(radio => {
      const optionLabel = this.getOptionLabel(radio);
      const optionPrice = this.getOptionPrice(radio);
      
      const option = document.createElement('option');
      option.value = radio.value;
      option.textContent = `${optionLabel} - ${optionPrice}`;
      
      this.stickySelect.appendChild(option);
      
      // Store option data
      this.subscriptionOptions.push({
        value: radio.value,
        label: optionLabel,
        price: optionPrice,
        radio: radio
      });
    });
  }

  getOptionLabel(radio) {
    const optionContainer = radio.closest('.subscription-option');
    const titleElement = optionContainer?.querySelector('.subscription-option__title');
    return titleElement?.textContent?.trim() || radio.value;
  }

  getOptionPrice(radio) {
    const optionContainer = radio.closest('.subscription-option');
    const priceElement = optionContainer?.querySelector('.subscription-option__total-price');
    return priceElement?.textContent?.trim() || '';
  }

  handleSubscriptionChange(event) {
    const { selectedOption } = event.detail;
    this.currentSelection = selectedOption;
    
    // Update select dropdown to match radio selection
    if (this.stickySelect) {
      this.stickySelect.value = selectedOption.type;
    }
  }

  handleSelectChange(event) {
    const selectedValue = event.target.value;
    
    // Find and trigger the corresponding radio button
    const correspondingRadio = this.subscriptionSelector?.querySelector(`input[value="${selectedValue}"]`);
    if (correspondingRadio) {
      correspondingRadio.checked = true;
      correspondingRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncFromRadioButtons() {
    if (!this.subscriptionSelector || !this.stickySelect) return;
    
    const checkedRadio = this.subscriptionSelector.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      this.stickySelect.value = checkedRadio.value;
    }
  }

  handleVariantChange(event) {
    // When variant changes, we need to update the select options with new prices
    // and update the variant title
    const variant = event.detail?.variant;
    
    if (variant) {
      // Update the variant title in sticky ATC
      this.updateVariantTitle(variant.title);
    }
    
    // Wait a short moment for the subscription selector to update its prices first
    setTimeout(() => {
      this.updateSelectOptions();
    }, 100);
  }

  updateSelectOptions() {
    if (!this.stickySelect || !this.subscriptionSelector) return;
    this.populateSelectOptions();
    this.syncFromRadioButtons();
  }

  updateVariantTitle(variantTitle) {
    const variantElement = this.stickyATC?.querySelector('.sticky-atc-button__variant');
    if (variantElement && variantTitle) {
      variantElement.textContent = variantTitle;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Delay initialization slightly to ensure subscription selector is ready
    setTimeout(() => {
      new StickyATC();
    }, 100);
  });
} else {
  setTimeout(() => {
    new StickyATC();
  }, 100);
}
